import React, { useRef, useState, useEffect } from 'react';

import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import { useActions } from 'hooks/useActions';
import { Images } from 'assets/images';
import * as ProfilePageActions from '../ProfilePage/actions';
import * as PasswordPageActions from 'global/actions/passwordActions';
import { phoneNumberRegex } from 'constants/regex';
import ResetPassword from './ResetPassword';
import ActivateTwoFactor from './ActivateTwoFactor';
import {
  DepartmentHeadingContainer,
  FlexContainer,
  OuterContainer,
  ContainerHeading,
  CompanyPhotoContainer,
  CompanyPhoto,
  CompanyPhotoInput,
  UploadPhotoLabel,
  FieldsContainer,
  ErrorMessage,
  TextInput,
} from './styles';
import { Button } from 'components/Button/RoundButton';

export default React.memo(() => {
  let { user } = useSelector((state) => state.auth);
  let { profile, loading, success } = useSelector((state) => state.profile);
  let { error: updatePasswordError, updatePasswordSuccess } = useSelector((state) => state.managePassword);

  const { updateUser, setProfile } = useActions(ProfilePageActions);
  const { resetPassword, updatePassword } = useActions(PasswordPageActions);

  const hiddenFileInput = useRef(null);
  const [formError, setFormError] = useState('');
  const [isHoveredOnLogo, setIsHoveredOnLogo] = useState(false);
  const [thumbnail, setThumbnail] = useState(user?.thumbnail ?? '');
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [city, setCity] = useState(user?.city ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showActivateTwoFactorModal, setShowActivateTwoFactorModal] = useState(false);

  if (thumbnail === '/images/placeholder-avatar-thumbnail.png') setThumbnail(Images.defaultAvatarPlaceholder);

  useEffect(() => {
    if (success) toast.success('Profile updated successfully');
  }, [success]);

  useEffect(() => {
    if (updatePasswordError) toast.error(updatePasswordError);
  }, [updatePasswordError]);

  useEffect(() => {
    if (updatePasswordSuccess) {
      setShowResetPasswordModal(false);
      toast.success('Password updated successfully');
    }
  }, [updatePasswordSuccess]);

  const validateForm = () => {
    if (!name) setFormError('Name is required');
    else if (phone && !String(phone).match(phoneNumberRegex)) setFormError('Phone number is not valid');
    else {
      setFormError('');
      return true;
    }
  };

  return (
    <OuterContainer>
      {showResetPasswordModal && (
        <ResetPassword
          user={user}
          onCancel={() => setShowResetPasswordModal(false)}
          onConfirm={(response) => updatePassword(response)}
          resetPasswordViaEmail={() => {
            resetPassword(user.email);
            setShowResetPasswordModal(false);
            toast.success('Success! An email has been sent to you.');
          }}
        />
      )}
      {showActivateTwoFactorModal && <ActivateTwoFactor closeModal={() => setShowActivateTwoFactorModal(false)} />}
      <DepartmentHeadingContainer>
        <ContainerHeading>Profile Settings</ContainerHeading>
        <FlexContainer>
          <Button
            small
            primaryBlue
            primaryGreenBorder
            margin="0 10px 0 0"
            onClick={() => setShowResetPasswordModal(true)}
          >
            Reset Password
          </Button>
          {!user.isTwoFactorAuthEnabled && (
            <Button
              small
              primaryBlue
              primaryGreenBorder
              removeMargin
              onClick={() => setShowActivateTwoFactorModal(true)}
            >
              Activate Two Factor Authentication
            </Button>
          )}
        </FlexContainer>
      </DepartmentHeadingContainer>
      <CompanyPhotoContainer>
        <CompanyPhoto
          image={thumbnail}
          onMouseEnter={() => setIsHoveredOnLogo(true)}
          onMouseLeave={() => setIsHoveredOnLogo(false)}
          onClick={() => hiddenFileInput.current.click()}
        >
          {isHoveredOnLogo && (
            <UploadPhotoLabel>
              <i className="fa fa-camera mr-1"></i>Upload
            </UploadPhotoLabel>
          )}
        </CompanyPhoto>

        <CompanyPhotoInput
          ref={hiddenFileInput}
          onChange={(e) => {
            let type = e.target.files[0].type;
            if (!['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'].includes(type))
              return toast.error('Please upload file of image type');
            const reader = new FileReader();
            reader.readAsDataURL(e.target.files[0]);
            reader.onload = () => {
              setThumbnail(reader.result);
              setProfile('avatar', reader.result, true);
            };
          }}
        />
      </CompanyPhotoContainer>
      <FieldsContainer>
        <TextInput
          value={name}
          isEditing={true}
          placeholder="Name*"
          onChange={(e) => {
            setName(e.target.value);
            setProfile('name', e.target.value, true);
          }}
        />
        <TextInput value={email} placeholder="Email*" onChange={(e) => setEmail(e.target.value)} />
        <TextInput
          value={phone}
          isEditing={true}
          placeholder="Phone"
          onChange={(e) => {
            setPhone(e.target.value);
            setProfile('phone', e.target.value, true);
          }}
        />
        <TextInput
          value={city}
          isEditing={true}
          placeholder="City"
          onChange={(e) => {
            setCity(e.target.value);
            setProfile('city', e.target.value, true);
          }}
        />
      </FieldsContainer>
      {formError && <ErrorMessage>{formError}</ErrorMessage>}
      <Button
        small
        primaryBlue
        primaryGreenBorder
        removeMargin
        disabled={loading}
        margin="10px 0 0 10px"
        onClick={() => {
          if (!validateForm()) return;
          updateUser(profile);
        }}
      >
        Save
      </Button>
    </OuterContainer>
  );
});
